import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  selectBookingPageSettings,
  selectBookingPageSettingsError,
  selectBookingPageSettingsStatus,
} from "../../features/trial-requests/trial-requests-slice";
import { updateBookingPageSettings } from "../../features/trial-requests/slice-extensions/trial-requests-slice-extension";
import { BookingPageLayout } from "../../features/trial-requests/enums/BookingPageLayout";
import { toUpdateRequest } from "../../features/trial-requests/utils/bookingPage";
import RichTextEditor from "./RichTextEditor";

const emptyToNull = (value: string): string | null => (value.trim() === "" ? null : value.trim());

/**
 * The page style (compact booking page vs full landing page) and the landing
 * page's own words: headline, about text, location line and whether the
 * weekly opening hours are shown.
 */
const WebsiteContentCard = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const settings = useAppSelector(selectBookingPageSettings);
  const status = useAppSelector(selectBookingPageSettingsStatus);
  const error = useAppSelector(selectBookingPageSettingsError);
  const [layout, setLayout] = useState<BookingPageLayout>("Booking");
  const [headline, setHeadline] = useState("");
  const [aboutText, setAboutText] = useState("");
  const [locationText, setLocationText] = useState("");
  const [showOpeningHours, setShowOpeningHours] = useState(false);
  const [accentColor, setAccentColor] = useState<string | null>(null);

  useEffect(() => {
    if (!settings) return;
    setLayout(settings.layout);
    setHeadline(settings.headline ?? "");
    setAboutText(settings.aboutText ?? "");
    setLocationText(settings.locationText ?? "");
    setShowOpeningHours(settings.showOpeningHours);
    setAccentColor(settings.accentColor ?? null);
  }, [settings]);

  const dirty =
    !!settings &&
    (layout !== settings.layout ||
      emptyToNull(headline) !== (settings.headline ?? null) ||
      emptyToNull(aboutText) !== (settings.aboutText ?? null) ||
      emptyToNull(locationText) !== (settings.locationText ?? null) ||
      showOpeningHours !== settings.showOpeningHours ||
      accentColor !== (settings.accentColor ?? null));

  const handleSave = () => {
    if (!settings) return;
    dispatch(
      updateBookingPageSettings({
        ...toUpdateRequest(settings),
        layout,
        headline: emptyToNull(headline),
        aboutText: emptyToNull(aboutText),
        locationText: emptyToNull(locationText),
        showOpeningHours,
        accentColor,
      }),
    );
  };

  if (!settings?.enabled) {
    return null;
  }

  const layoutOption = (value: BookingPageLayout, label: string, hint: string) => (
    <button
      type="button"
      onClick={() => setLayout(value)}
      className={
        layout === value
          ? "flex-1 rounded-xl border-2 border-primary-40 bg-primary-95 p-3 text-left dark:border-primary-90 dark:bg-primary-20"
          : "flex-1 rounded-xl border-2 border-grey-90 bg-white p-3 text-left hover:border-grey-70 dark:border-grey-50 dark:bg-primary-10 dark:hover:border-grey-70"
      }
    >
      <span className="block text-sm font-semibold text-grey-20 dark:text-white">{label}</span>
      <span className="mt-0.5 block text-xs text-grey-40 dark:text-grey-80">{hint}</span>
    </button>
  );

  return (
    <div className="with-border rounded-2xl bg-white p-4 dark:bg-primary-10 lg:p-6">
      <h2 className="text-lg font-semibold text-grey-20 dark:text-white">
        {t("website.contentTitle", "Page style & content")}
      </h2>
      <p className="mt-1 text-sm text-grey-40 dark:text-grey-80">
        {t("website.contentIntro", "Choose what visitors see: a compact booking page, or a full landing page with your photos, story and services.")}
      </p>

      <label className="mt-4 flex flex-col gap-1 text-sm font-medium text-grey-30 dark:text-grey-70">
        {t("website.accentColorLabel", "Accent color")}
        <span className="flex items-center gap-2">
          <input
            type="color"
            value={accentColor ?? "#7c3aed"}
            onChange={(event) => setAccentColor(event.target.value)}
            className="h-9 w-14 cursor-pointer rounded-lg border border-grey-90 bg-white dark:border-grey-50 dark:bg-primary-20"
            aria-label={t("website.accentColorLabel", "Accent color")}
          />
          {accentColor && (
            <button
              type="button"
              className="shrink-0 text-sm text-primary-40 hover:underline dark:text-primary-90"
              onClick={() => setAccentColor(null)}
            >
              {t("website.accentReset", "Reset to default")}
            </button>
          )}
          <span className="text-xs font-normal text-grey-40 dark:text-grey-80">
            {t("website.accentHint", "Your color, everywhere: buttons and highlights on your page and in the widgets.")}
          </span>
        </span>
      </label>

      <div className="mt-4 flex flex-col gap-2 sm:flex-row">
        {layoutOption(
          "Booking",
          t("website.layoutBooking", "Booking page"),
          t("website.layoutBookingHint", "Compact — the trial-request form front and center."),
        )}
        {layoutOption(
          "Landing",
          t("website.layoutLanding", "Landing page"),
          t("website.layoutLandingHint", "Your personal website — hero image, about, services, and booking built in."),
        )}
      </div>

      {/* Headline + location render on the landing page AND the links page, so
          they stay editable on both layouts. */}
      <label className="mt-4 block text-sm font-medium text-grey-30 dark:text-grey-70">
        {t("website.headlineLabel", "Headline")}
        <input
          className="input mt-1"
          value={headline}
          maxLength={120}
          onChange={(event) => setHeadline(event.target.value)}
          placeholder={t("website.headlinePlaceholder", "e.g. Piano lessons that stick — patient, personal, fun")}
        />
        <span className="mt-1 block text-xs font-normal text-grey-40 dark:text-grey-80">
          {t("website.headlineHint", "Shown under your name on the landing page and your links page.")}
        </span>
      </label>

      {layout === "Landing" && (
        <div className="mt-4 block text-sm font-medium text-grey-30 dark:text-grey-70">
          {t("website.aboutLabel", "About you")}
          <RichTextEditor value={aboutText} onChange={setAboutText} />
          <p className="mt-1 text-xs font-normal text-grey-40 dark:text-grey-80">
            {t("website.aboutHint", "Tell your story — formatting (bold, lists, headings) carries over to your page.")}
          </p>
        </div>
      )}

      <label className="mt-4 block text-sm font-medium text-grey-30 dark:text-grey-70">
        {t("website.locationLabel", "Location")}
        <input
          className="input mt-1"
          value={locationText}
          maxLength={160}
          onChange={(event) => setLocationText(event.target.value)}
          placeholder={t("website.locationPlaceholder", "e.g. Studio in Hamburg-Altona · online worldwide")}
        />
      </label>

      {layout === "Landing" && (
        <label className="mt-4 flex items-center gap-2 text-sm font-medium text-grey-30 dark:text-grey-70">
          <input
            type="checkbox"
            checked={showOpeningHours}
            onChange={(event) => setShowOpeningHours(event.target.checked)}
          />
          {t("website.showOpeningHours", "Show my weekly working hours on the page")}
        </label>
      )}

      {error && <p className="field-error mt-2">{error}</p>}

      {dirty && (
        <button
          type="button"
          className="btn btn-primary mt-4"
          onClick={handleSave}
          disabled={status === "loading"}
        >
          {t("website.save", "Save changes")}
        </button>
      )}
    </div>
  );
};

export default WebsiteContentCard;
